import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/apiConfig';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  AcademicCapIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const MatricSubjects = ({ user }) => {
  const { accessToken, makeAuthenticatedRequest } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    achievement_level: '',
    percentage: '',
    year_completed: new Date().getFullYear()
  });

  const requestApi = async (path, options = {}) => {
    const response = await makeAuthenticatedRequest(`${API_CONFIG.API_URL}${path}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const requestError = new Error(data.detail || `Request failed (${response.status})`);
      requestError.response = { status: response.status, data };
      throw requestError;
    }
    return { data, status: response.status };
  };

  // South African school subjects (from backend model)
  const SUBJECT_CHOICES = [
    // Core Subjects
    { value: 'mathematics', label: 'Mathematics' },
    { value: 'mathematical_literacy', label: 'Mathematical Literacy' },
    { value: 'life_orientation', label: 'Life Orientation' },

    // Languages - Home Language
    { value: 'english_hl', label: 'English Home Language' },
    { value: 'afrikaans_hl', label: 'Afrikaans Home Language' },
    { value: 'isizulu_hl', label: 'isiZulu Home Language' },
    { value: 'isixhosa_hl', label: 'isiXhosa Home Language' },
    { value: 'sepedi_hl', label: 'Sepedi Home Language' },
    { value: 'setswana_hl', label: 'Setswana Home Language' },
    { value: 'sesotho_hl', label: 'Sesotho Home Language' },
    { value: 'xitsonga_hl', label: 'Xitsonga Home Language' },
    { value: 'siswati_hl', label: 'siSwati Home Language' },
    { value: 'tshivenda_hl', label: 'Tshivenda Home Language' },
    { value: 'isindebele_hl', label: 'isiNdebele Home Language' },

    // Languages - First Additional Language
    { value: 'english_fal', label: 'English First Additional Language' },
    { value: 'afrikaans_fal', label: 'Afrikaans First Additional Language' },
    { value: 'isizulu_fal', label: 'isiZulu First Additional Language' },
    { value: 'isixhosa_fal', label: 'isiXhosa First Additional Language' },
    { value: 'sepedi_fal', label: 'Sepedi First Additional Language' },
    { value: 'setswana_fal', label: 'Setswana First Additional Language' },
    { value: 'sesotho_fal', label: 'Sesotho First Additional Language' },
    { value: 'xitsonga_fal', label: 'Xitsonga First Additional Language' },
    { value: 'siswati_fal', label: 'siSwati First Additional Language' },
    { value: 'tshivenda_fal', label: 'Tshivenda First Additional Language' },
    { value: 'isindebele_fal', label: 'isiNdebele First Additional Language' },

    // Languages - Second Additional Language
    { value: 'english_sal', label: 'English Second Additional Language' },
    { value: 'afrikaans_sal', label: 'Afrikaans Second Additional Language' },
    { value: 'isizulu_sal', label: 'isiZulu Second Additional Language' },
    { value: 'isixhosa_sal', label: 'isiXhosa Second Additional Language' },
    { value: 'sepedi_sal', label: 'Sepedi Second Additional Language' },
    { value: 'setswana_sal', label: 'Setswana Second Additional Language' },
    { value: 'sesotho_sal', label: 'Sesotho Second Additional Language' },
    { value: 'xitsonga_sal', label: 'Xitsonga Second Additional Language' },
    { value: 'siswati_sal', label: 'siSwati Second Additional Language' },
    { value: 'tshivenda_sal', label: 'Tshivenda Second Additional Language' },
    { value: 'isindebele_sal', label: 'isiNdebele Second Additional Language' },

    // Sciences
    { value: 'physical_sciences', label: 'Physical Sciences' },
    { value: 'life_sciences', label: 'Life Sciences' },
    { value: 'agricultural_sciences', label: 'Agricultural Sciences' },

    // Commercial Subjects
    { value: 'accounting', label: 'Accounting' },
    { value: 'business_studies', label: 'Business Studies' },
    { value: 'economics', label: 'Economics' },

    // Humanities
    { value: 'geography', label: 'Geography' },
    { value: 'history', label: 'History' },

    // Technology
    { value: 'information_technology', label: 'Information Technology' },
    { value: 'computer_applications_technology', label: 'Computer Applications Technology' },
    { value: 'engineering_graphics_design', label: 'Engineering Graphics and Design' },
    { value: 'civil_technology', label: 'Civil Technology' },
    { value: 'electrical_technology', label: 'Electrical Technology' },
    { value: 'mechanical_technology', label: 'Mechanical Technology' },

    // Consumer Studies
    { value: 'consumer_studies', label: 'Consumer Studies' },
    { value: 'hospitality_studies', label: 'Hospitality Studies' },
    { value: 'tourism', label: 'Tourism' },

    // Arts and Culture
    { value: 'dramatic_arts', label: 'Dramatic Arts' },
    { value: 'music', label: 'Music' },
    { value: 'visual_arts', label: 'Visual Arts' },
    { value: 'dance_studies', label: 'Dance Studies' },

    // Additional Subjects
    { value: 'religion_studies', label: 'Religion Studies' },
    { value: 'maritime_economics', label: 'Maritime Economics' },
    { value: 'nautical_science', label: 'Nautical Science' },
    { value: 'design', label: 'Design' },
    { value: 'equine_studies', label: 'Equine Studies' },
    { value: 'sports_coaching_science', label: 'Sports and Exercise Science' }
  ];

  const ACHIEVEMENT_LEVELS = [
    { value: '7', label: 'Level 7 (80-100%)', color: '#10B981' },
    { value: '6', label: 'Level 6 (70-79%)', color: '#059669' },
    { value: '5', label: 'Level 5 (60-69%)', color: '#0D9488' },
    { value: '4', label: 'Level 4 (50-59%)', color: '#0891B2' },
    { value: '3', label: 'Level 3 (40-49%)', color: '#F59E0B' },
    { value: '2', label: 'Level 2 (30-39%)', color: '#EF4444' },
    { value: '1', label: 'Level 1 (0-29%)', color: '#DC2626' }
  ];

  useEffect(() => {
    console.log('🔄 MatricSubjects useEffect triggered');
    console.log('👤 User:', user);
    if (accessToken) {
      fetchSubjects();
    } else {
      setSubjects([]);
      setLoading(false);
    }
  }, [accessToken]);

  const fetchSubjects = async () => {
    if (!accessToken) {
      setSubjects([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('📡 Fetching matric subjects...');

      const response = await requestApi('/auth/matric-subjects/');

      // Handle paginated response - extract results array
      let subjectsData = [];
      if (response.data && Array.isArray(response.data.results)) {
        subjectsData = response.data.results;
      } else if (Array.isArray(response.data)) {
        subjectsData = response.data;
      }

      console.log(`✅ Setting ${subjectsData.length} subjects:`, subjectsData);
      setSubjects(subjectsData);
    } catch (err) {
      console.error('❌ Error fetching matric subjects:', err);
      console.error('📊 Error status:', err.response?.status);
      setError(err.response?.status === 401
        ? 'Your session has expired. Please sign in again to continue.'
        : err.response?.data?.detail || 'Failed to load matric subjects');
      // Set empty array on error to prevent map errors
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate percentage based on achievement level
    if (name === 'achievement_level') {
      const levelRanges = {
        '7': 90, '6': 75, '5': 65, '4': 55, '3': 45, '2': 35, '1': 25
      };
      setFormData(prev => ({
        ...prev,
        percentage: levelRanges[value] || ''
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const validateForm = () => {
    if (!formData.subject) {
      setError('Please select a subject');
      return false;
    }
    if (!formData.achievement_level) {
      setError('Please select an achievement level');
      return false;
    }
    if (formData.percentage && (formData.percentage < 0 || formData.percentage > 100)) {
      setError('Percentage must be between 0 and 100');
      return false;
    }
    if (!formData.year_completed || formData.year_completed < 1990 || formData.year_completed > new Date().getFullYear()) {
      setError('Please enter a valid year');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setError('');
      console.log('🔄 Submitting matric subject:', formData);

      const url = editingSubject
        ? `/api/auth/matric-subjects/${editingSubject.id}/`
        : '/api/auth/matric-subjects/';

      const method = editingSubject ? 'put' : 'post';

      console.log(`📡 Making ${method.toUpperCase()} request to:`, url);
      console.log('📦 Request data:', formData);

      const response = await requestApi(url.replace('/api', ''), {
        method: method.toUpperCase(),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      console.log('✅ Subject saved successfully:', response.data);

      // Refresh the subjects list
      await fetchSubjects();
      resetForm();
      setError('');

      console.log('🔄 Form reset and subjects refreshed');
    } catch (err) {
      console.error('❌ Error saving subject:', err);
      console.error('📄 Error response:', err.response?.data);
      console.error('📊 Error status:', err.response?.status);

      if (err.response?.data?.subject) {
        setError('You have already added this subject');
      } else if (err.response?.data?.non_field_errors) {
        setError(err.response.data.non_field_errors[0]);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(err.response?.status === 401
          ? 'Your session has expired. Please sign in again to continue.'
          : err.response?.data?.detail || `Failed to save subject: ${err.response?.status || 'Unknown error'}`);
      }
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subject: subject.subject,
      achievement_level: subject.achievement_level,
      percentage: subject.percentage || '',
      year_completed: subject.year_completed
    });
    setShowAddForm(true);
  };

  const handleDelete = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;

    try {
      await requestApi(`/auth/matric-subjects/${subjectId}/`, { method: 'DELETE' });
      await fetchSubjects();
    } catch (err) {
      console.error('Error deleting subject:', err);
      setError('Failed to delete subject');
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      achievement_level: '',
      percentage: '',
      year_completed: new Date().getFullYear()
    });
    setEditingSubject(null);
    setShowAddForm(false);
    setError('');
  };

  const getAchievementColor = (level) => {
    const achievement = ACHIEVEMENT_LEVELS.find(a => a.value === level);
    return achievement?.color || '#6B7280';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ color: '#6B7280' }}>Loading matric subjects...</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <h5 style={{ 
          margin: 0, 
          color: 'var(--primary-black)', 
          fontSize: '16px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AcademicCapIcon style={{ width: '20px', height: '20px', color: 'var(--primary-yellow)' }} />
          Matric Subjects & Results
        </h5>
        
        {!showAddForm && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: 'var(--primary-yellow)',
                color: 'var(--primary-black)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#E5B800'}
              onMouseOut={(e) => e.target.style.background = 'var(--primary-yellow)'}
            >
              <PlusIcon style={{ width: '16px', height: '16px' }} />
              Add Subject
            </button>

            {/* Debug button - remove in production */}
            <button
              onClick={() => {
                console.log('🔍 Debug Info:');
                console.log('User:', user);
                console.log('Subjects:', subjects);
                console.log('Loading:', loading);
                console.log('Error:', error);
                fetchSubjects();
              }}
              style={{
                padding: '8px 12px',
                background: '#F3F4F6',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              title="Debug: Check console and refresh subjects"
            >
              🔍 Debug
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '12px',
          background: '#FEE2E2',
          border: '1px solid #FECACA',
          borderRadius: '8px',
          color: '#DC2626',
          fontSize: '14px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div style={{
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h6 style={{ 
            margin: '0 0 16px 0', 
            color: 'var(--primary-black)',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {editingSubject ? 'Edit Subject' : 'Add New Subject'}
          </h6>

          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: 'var(--primary-black)'
                }}>
                  Subject *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="">Select a subject</option>
                  {SUBJECT_CHOICES.map(subject => (
                    <option key={subject.value} value={subject.value}>
                      {subject.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: 'var(--primary-black)'
                }}>
                  Achievement Level *
                </label>
                <select
                  name="achievement_level"
                  value={formData.achievement_level}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="">Select achievement level</option>
                  {ACHIEVEMENT_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: 'var(--primary-black)'
                }}>
                  Percentage (Optional)
                </label>
                <input
                  type="number"
                  name="percentage"
                  value={formData.percentage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  min="0"
                  max="100"
                  placeholder="e.g. 85"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: 'var(--primary-black)'
                }}>
                  Year Completed *
                </label>
                <input
                  type="number"
                  name="year_completed"
                  value={formData.year_completed}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  min="1990"
                  max={new Date().getFullYear()}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={handleSubmit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: 'var(--primary-yellow)',
                  color: 'var(--primary-black)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <CheckIcon style={{ width: '16px', height: '16px' }} />
                {editingSubject ? 'Update Subject' : 'Add Subject'}
              </button>

              <button
                type="button"
                onClick={resetForm}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <XMarkIcon style={{ width: '16px', height: '16px' }} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subjects List */}
      {!Array.isArray(subjects) || subjects.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          color: '#6B7280'
        }}>
          <AcademicCapIcon style={{ 
            width: '48px', 
            height: '48px', 
            margin: '0 auto 16px', 
            color: '#D1D5DB' 
          }} />
          <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
            No matric subjects added yet
          </p>
          <p style={{ margin: 0, fontSize: '14px' }}>
            Add your matric subjects and results to complete your education profile
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {Array.isArray(subjects) && subjects.map(subject => (
            <div
              key={subject.id}
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: 'var(--primary-black)',
                  marginBottom: '4px'
                }}>
                  {subject.subject_display}
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    background: getAchievementColor(subject.achievement_level),
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Level {subject.achievement_level}
                  </span>
                  {subject.percentage && (
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      {subject.percentage}%
                    </span>
                  )}
                  <span style={{ fontSize: '14px', color: '#6B7280' }}>
                    {subject.year_completed}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleEdit(subject)}
                  style={{
                    padding: '8px',
                    background: '#F3F4F6',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Edit subject"
                >
                  <PencilIcon style={{ width: '16px', height: '16px', color: '#6B7280' }} />
                </button>

                <button
                  onClick={() => handleDelete(subject.id)}
                  style={{
                    padding: '8px',
                    background: '#FEE2E2',
                    border: '1px solid #FECACA',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Delete subject"
                >
                  <TrashIcon style={{ width: '16px', height: '16px', color: '#DC2626' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatricSubjects;
